// Fuel top-off request — an sms: deep link, nothing more.
//
// AJM is deliberately NOT in the fuel supply chain: under a dry lease the lessee
// provides fuel. The one exception is a discovery flight (fuel included, Casey 10/26/26): the
// CFI (the guest's hired PIC; the guest rents the aircraft) asks Signature to bill the aircraft's card. So this builds a message the PILOT sends from their OWN phone,
// asking the FBO to call THEM for a card. Outside discovery flights AJM never buys fuel or rebills it,
// and nothing about the request is written to Firestore — no request log, no
// statement line, no charge. The button is a link and that is the whole feature.

/** Signature's text-for-fuel line at KLGB (from Gabe, 2026-08-21). */
export const FUEL_SMS_NUMBER = '+15622964857';

/** Signature's front-desk line at KLGB — some pilots would rather just call. */
export const FUEL_CALL_NUMBER = '562-997-0700';

/** `tel:` needs the digits only, E.164. */
export function buildFuelCallHref(): string {
  return `tel:+1${FUEL_CALL_NUMBER.replace(/\D/g, '')}`;
}

/** Who we're texting. */
export const FUEL_FBO = 'Signature Aviation';

/** Where N8366X sits — the FBO it's tied down at, and the spot. */
export const FUEL_PARKING = 'Million Air North, C3';

export type FuelRequestArgs = {
  /** Pilot's full name, as the FBO should hear it on the callback. */
  pilotName: string;
  tailNumber: string;
  /** Owner flights skip the lessee framing and the card-on-file warning. */
  isOwner?: boolean;
  /**
   * Discovery flights include fuel (Casey, 10/26/26): AJM pays the top-off, so the CFI asks
   * for the aircraft's card, close to Casey's own wording, from the CFI's phone.
   */
  discovery?: boolean;
  /**
   * Demo mode drops the recipient so Messages opens with the body prefilled and
   * nobody to send it to — you can read the real thing on a real phone with no
   * way to fire it at the FBO by accident.
   */
  demo?: boolean;
};

export function buildFuelMessage({ pilotName, tailNumber, isOwner, discovery }: FuelRequestArgs): string {
  if (discovery && !isOwner)
    // Not "flying for the owner": the guest rents the aircraft and hired this CFI as PIC; the CFI is never AJM's pilot.
    return `Hello ${FUEL_FBO} - this is ${pilotName}, flight instructor in ${tailNumber} today, parked at ${FUEL_PARKING}. Requesting a top off. Please bill the owner's card on file for this aircraft. Thank you!`;
  const who = isOwner ? `owner of ${tailNumber}` : `lessee of ${tailNumber}`;
  const opening = `Hello ${FUEL_FBO} - this is ${pilotName}, ${who}, parked at ${FUEL_PARKING}. Requesting a top off.`;
  // Line crews default to billing whatever card is on the aircraft record. That is
  // correct for the owner's own flights and wrong for a lessee's, so each says so
  // explicitly rather than relying on the FBO to guess.
  const payment = isOwner
    ? 'Please bill the card on file for this aircraft.'
    : "Please do not bill the card on file for this aircraft - call me at this number and I'll provide my own card for payment.";
  return `${opening} ${payment} Thank you!`;
}

/**
 * `sms:+1...?&body=` is the form that prefills on both iOS and Android — iOS
 * wants the `&` before the first param, Android tolerates it.
 */
export function buildFuelSmsHref(args: FuelRequestArgs): string {
  const to = args.demo ? '' : FUEL_SMS_NUMBER;
  return `sms:${to}?&body=${encodeURIComponent(buildFuelMessage(args))}`;
}
